/**
 * Copyright 2025 Lumavec Group LLC
 *
 * TemplateOptionsForm Component
 * 
 * Renders a form for configuring template options.
 */

import { Label } from '../ui/label';
import { Select } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import type { TemplateMetadata, TemplateOptions, TemplateOption } from '../../lib/api';

interface TemplateOptionsFormProps {
  template: TemplateMetadata;
  options: TemplateOptions;
  onChange: (options: TemplateOptions) => void;
}

export function TemplateOptionsForm({ template, options, onChange }: TemplateOptionsFormProps) {
  const handleChange = (key: string, value: string | boolean | string[]) => {
    onChange({ ...options, [key]: value });
  };
  
  const optionEntries = Object.entries(template.options);
  
  if (optionEntries.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        This template has no configurable options.
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {optionEntries.map(([key, option]) => (
        <div key={key} className="space-y-2">
          <Label htmlFor={key} className="text-sm font-medium">
            {option.label}
          </Label>
          
          {option.type === 'select' && option.choices && (
            <select
              id={key}
              value={(options as Record<string, string>)[key] ?? option.default as string}
              onChange={(e) => handleChange(key, e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {option.choices.map((choice) => (
                <option key={choice} value={choice}>
                  {formatChoiceLabel(choice)}
                </option>
              ))}
            </select>
          )}
          
          {option.type === 'boolean' && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id={key}
                checked={(options as Record<string, boolean>)[key] ?? option.default as boolean}
                onCheckedChange={(checked) => handleChange(key, checked === true)}
              />
              <label
                htmlFor={key}
                className="text-sm text-muted-foreground cursor-pointer"
              >
                {option.description || `Enable ${option.label}`}
              </label>
            </div>
          )}
          
          {option.type === 'multiselect' && option.choices && (
            <div className="space-y-2 pl-1">
              {option.choices.map((choice) => {
                const currentValues = ((options as Record<string, string[]>)[key] ?? option.default) as string[];
                const isChecked = currentValues.includes(choice);
                
                return (
                  <div key={choice} className="flex items-center space-x-2">
                    <Checkbox
                      id={`${key}-${choice}`}
                      checked={isChecked}
                      onCheckedChange={(checked) => {
                        const newValues = checked
                          ? [...currentValues, choice]
                          : currentValues.filter((v) => v !== choice);
                        handleChange(key, newValues);
                      }}
                    />
                    <label
                      htmlFor={`${key}-${choice}`}
                      className="text-sm text-muted-foreground cursor-pointer"
                    >
                      {formatChoiceLabel(choice)}
                    </label>
                  </div>
                );
              })}
            </div>
          )}
          
          {option.description && option.type !== 'boolean' && (
            <p className="text-xs text-muted-foreground mt-1">
              {option.description}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

function formatChoiceLabel(choice: string): string {
  // Handle common abbreviations and format nicely
  const mapping: Record<string, string> = {
    'sqlite': 'SQLite',
    'postgres': 'PostgreSQL',
    'none': 'None',
    'jwt': 'JWT',
    'firebase': 'Firebase',
    'shadcn': 'shadcn/ui',
    'minimal': 'Minimal',
    'custom': 'Custom',
    'react-router': 'React Router',
    'wouter': 'Wouter',
    'react-query': 'React Query',
    'zustand': 'Zustand',
    'context-only': 'Context Only',
    'platform': 'Platform (SkillShop)',
    'local': 'Local',
    'auth': 'Authentication',
    'database': 'Database',
    'ai': 'AI Agents',
    'storage': 'Storage',
    'chat': 'Chat',
  };
  
  return mapping[choice] || choice.charAt(0).toUpperCase() + choice.slice(1).replace(/-/g, ' ');
}

export default TemplateOptionsForm;

